import React, { useMemo } from 'react'
import * as R from 'ramda'
import { mutualTheme } from '~/utils/theme'
import { formatDateTime } from '~/utils/date'
import { CellProps, Column } from 'react-table'
import { makeStyles } from '@material-ui/styles'
import { PendenciesActions } from './components'
import { ServiceResponse } from '@mutual-libs/shared-types'
import { translateDocumentType } from '~/utils/documents'
import { DocumentStatusBadge, Table } from '~/components'
import { Box, TableContainer, Typography } from '@material-ui/core'
import { DocumentsGroup, DocumentAnalysisStatus, DocumentsGroupType } from '@mutual-services/account-documents-types'
import { useIsMobileScreen } from '~/hooks'

type PendenciesTableDocuments = ServiceResponse<DocumentsGroup>

export type PendenciesTableProps = {
  updateDocuments: () => void,
  documents: ServiceResponse<DocumentsGroup[]>
}

const getPendenciesHeader = (documents: ServiceResponse<DocumentsGroup[]>) => {
  const solvedPendencies = documents.filter(item => item.status === DocumentAnalysisStatus.APPROVED)
  const pendingPendencies = documents.filter(item => item.status === DocumentAnalysisStatus.PENDING)
  const validatingPendencies = documents.filter(item => item.status === DocumentAnalysisStatus.PENDING_ANALYSIS)

  const pendingPendenciesText = !R.isEmpty(pendingPendencies)
    ? `${pendingPendencies.length} pendência${pendingPendencies.length > 1 ? 's' : ''} em aberto`
    : 'Nenhuma pendência em aberto'

  const secondaryText = !R.isEmpty(validatingPendencies)
    ? `${validatingPendencies.length} pendência${validatingPendencies.length > 1 ? 's' : ''} em validação`
    : `${solvedPendencies.length} pendência${solvedPendencies.length > 1 ? 's' : ''} resolvida${solvedPendencies.length > 1 ? 's' : ''}`

  return (
    <Box>
      <Typography sx={{ fontWeight: 700 }} variant='airbnbH4'>{pendingPendenciesText}</Typography>
      {(!R.isEmpty(solvedPendencies) || !R.isEmpty(validatingPendencies)) && (
        <Typography sx={{ fontWeight: 400, color: mutualTheme.palette.textGray2.main }} variant='airbnbH4'> • {secondaryText}</Typography>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    alignItems: 'center',
    marginTop: mutualTheme.spacing(2),
    boxShadow: mutualTheme.boxShadow.container,
    borderRadius: mutualTheme.shape.borderRadius,
    backgroundColor: mutualTheme.palette.common.white
  }
})

export const PendenciesTable: React.FC<PendenciesTableProps> = (props) => {
  const { documents, updateDocuments } = props
  const classes = useStyles()
  const isMobile = useIsMobileScreen()

  const COLUMNS: Column<PendenciesTableDocuments>[] = useMemo(() => {
    return [
      {
        Header: 'Documento',
        Cell: ({ row: { original } }: CellProps<PendenciesTableDocuments>) => {
          const pendency = original.timeline[original.timeline.length - 1]
          const denialReason = pendency.denialReason || original.timeline[original.timeline.length - 2].denialReason
          const isAdditionalDocument = original.type === DocumentsGroupType.ADDITIONAL
          const additionalDocumentDenialReasonList = original.timeline.filter(item => item.denialReason !== null)
          const additionalDocumentDenialReason = !R.isEmpty(additionalDocumentDenialReasonList) ? additionalDocumentDenialReasonList[additionalDocumentDenialReasonList.length - 1].denialReason : original.typeDescription

          return (
            <Typography
              sx={{
                fontWeight: 400,
                color: pendency.status === DocumentAnalysisStatus.APPROVED
                  ? mutualTheme.palette.textGray2.main
                  : mutualTheme.palette.common.black
              }}
              variant={isMobile ? 'airbnbBody2' : 'airbnbH5'}
            >
              {<b>{isAdditionalDocument ? (original.typeName || original.type) : translateDocumentType(original.type)}</b>}
              {isMobile ? '' : `: ${isAdditionalDocument ? additionalDocumentDenialReason : (denialReason || '')}`}
            </Typography>
          )
        }
      },
      !isMobile && {
        Header: 'Última atualização',
        Cell: ({ row: { original } }: CellProps<PendenciesTableDocuments>) => {
          const pendency = original.timeline[original.timeline.length - 1]

          return (
            <Typography color={mutualTheme.palette.textGray2.main} variant='airbnbBody1'>
              {formatDateTime(pendency.date)}
            </Typography>
          )
        }
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ row: { original } }: CellProps<PendenciesTableDocuments>) => {
          const pendency = original.timeline[original.timeline.length - 1]

          return <DocumentStatusBadge status={pendency.status} />
        }
      },
      {
        Header: 'Ações',
        Cell: ({ row: { original } }: CellProps<PendenciesTableDocuments>) => {
          const pendency = original.timeline[original.timeline.length - 1]
          const isActive = pendency.status === DocumentAnalysisStatus.PENDING

          return <PendenciesActions documents={documents} updateDocuments={updateDocuments} isActive={isActive} document={original} />
        }
      }
    ].filter(item => !!item) as any
  }, [documents, isMobile, translateDocumentType, formatDateTime])

  return (
    <Box sx={{ marginTop: 5 }}>
      {documents && getPendenciesHeader(documents)}
      <TableContainer className={classes.container}>
        <Table
          data={documents}
          columns={COLUMNS}
        />
      </TableContainer>
    </Box>
  )
}
